import {
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useGoLiveSteps } from "./useGoLiveSteps";
import { useMenuIdFromURL } from "../../../hooks/menuIdFromURL";
import { GoLiveSuccess } from "./GoLiveSuccess";

const collapsedKey = (menuId: string) => `checklistCollapsed:${menuId}`;

export const GoLiveChecklist = () => {
  const { steps, isComplete, restaurantName } = useGoLiveSteps();
  const menuId = useMenuIdFromURL();

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(collapsedKey(menuId)) === "true"
  );

  if (isComplete) return <GoLiveSuccess />;

  const completedCount = steps.filter((step) => step.done).length;
  const progressPercent = (completedCount / steps.length) * 100;
  const nextStep = steps.find((step) => !step.done);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem(collapsedKey(menuId), String(next));
  };

  return (
    <Paper elevation={2} sx={{ mb: 2, overflow: "hidden", borderRadius: 2 }}>
      <Box
        sx={{
          bgcolor: "primary.main",
          px: 3,
          py: 2,
          color: "white",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
            {restaurantName}
          </Typography>
          <Typography variant="subtitle1" fontWeight={600}>
            Get your menu live
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.25 }}>
            {`${completedCount} of ${steps.length} done`}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{
              mt: 1.5,
              height: 6,
              borderRadius: 3,
              bgcolor: "rgba(255,255,255,0.3)",
              "& .MuiLinearProgress-bar": { bgcolor: "white", borderRadius: 3 },
            }}
          />
        </Box>

        <Tooltip title={collapsed ? "Expand" : "Collapse"}>
          <IconButton
            size="small"
            onClick={toggleCollapsed}
            aria-label={collapsed ? "expand checklist" : "collapse checklist"}
            sx={{ color: "white", ml: 1, mt: 0.5 }}
          >
            {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {!collapsed && (
        <Box sx={{ px: 2, py: 1 }}>
          {steps.map((step) => {
            const isCurrent = step === nextStep;
            return (
              <Box
                key={step.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 0.75,
                  px: 1,
                  my: 0.25,
                  borderRadius: 1.5,
                  bgcolor: isCurrent
                    ? "rgba(25, 118, 210, 0.06)"
                    : "transparent",
                  borderLeft: "3px solid",
                  borderColor: isCurrent ? "primary.main" : "transparent",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  {step.done ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <RadioButtonUncheckedIcon
                      fontSize="small"
                      sx={{
                        color: isCurrent ? "primary.main" : "text.disabled",
                      }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      textDecoration: step.done ? "line-through" : "none",
                      color: step.done
                        ? "text.disabled"
                        : isCurrent
                        ? "text.primary"
                        : "text.secondary",
                      fontWeight: isCurrent ? 600 : 400,
                    }}
                  >
                    {step.label}
                  </Typography>
                  {isCurrent && (
                    <Chip
                      label="Next"
                      size="small"
                      color="primary"
                      sx={{ height: 18, fontSize: "0.65rem" }}
                    />
                  )}
                </Box>

                {isCurrent && step.action && (
                  <Button
                    component={Link}
                    to={step.action.to}
                    size="small"
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => step.action?.onActivate?.()}
                    sx={{ whiteSpace: "nowrap", ml: 2, flexShrink: 0 }}
                  >
                    {step.action.label}
                  </Button>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};
